import { Check, ChevronDown, GraduationCap, Layers } from "lucide-react";
import { toast } from "sonner";
import { useProgramme, type ProgrammeSlug } from "@/lib/useProgramme";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

// Topbar dropdown that lets tenants with multiple programmes enabled flip
// between them. Hidden entirely for tenants with only one programme (the
// default — most tenants won't have HEDA) so the chrome stays quiet.

const ICON_BY_SLUG: Record<ProgrammeSlug, typeof GraduationCap> = {
  fs_english: Layers,
  heda: GraduationCap,
};

const SHORT_BY_SLUG: Record<ProgrammeSlug, string> = {
  fs_english: "FS English",
  heda: "HEDA",
};

export function ProgrammeSwitcher() {
  const { programmes, active, setActive } = useProgramme();

  // Hide when the tenant has zero or one programmes enabled.
  if (!active || programmes.length <= 1) return null;

  const Icon = ICON_BY_SLUG[active.slug];

  function switchTo(slug: ProgrammeSlug) {
    if (slug === active?.slug) return;
    setActive(slug);
    // Soft state change — programme-aware pages (Dashboard,
    // AuthoringList, etc.) re-fetch via their useEffect deps on
    // active.slug. Don't forcibly nav away: the previous behaviour
    // dumped the user onto /dashboard from wherever they were
    // (Question Bank, Authoring editor, …) which made the switcher
    // feel broken when their current page wasn't even programme-aware.
    const target = programmes.find((p) => p.slug === slug);
    toast.success(`Switched to ${target?.name ?? slug}`);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          aria-label={`Switch programme (currently ${active.name})`}
        >
          <Icon className="h-4 w-4" aria-hidden />
          <span className="font-medium">{SHORT_BY_SLUG[active.slug]}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Switch programme
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {programmes.map((p) => {
          const PIcon = ICON_BY_SLUG[p.slug] ?? GraduationCap;
          const isActive = p.slug === active.slug;
          return (
            <DropdownMenuItem
              key={p.id}
              onClick={() => switchTo(p.slug)}
              className={cn(isActive && "font-semibold")}
            >
              <PIcon className="mr-2 h-4 w-4" aria-hidden />
              <div className="flex-1 truncate">
                <div className="truncate">{p.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {p.subject_labels.length} subjects · {p.level_codes.length} levels
                </div>
              </div>
              {isActive && <Check className="ml-2 h-4 w-4 text-primary" aria-hidden />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
